/** The sign-off on the Approvals tab: who attested what, and whether it still holds - P15 items 4, 5.
 *
 * **A card per full approval, permanent.** Name, role, the UTC time with the reader's local time
 * beside it, and the ledger entry that holds the decision. It is the answer to "who approved this
 * figure", and the thing an assurance provider asks for first. Everything on it is read from the
 * server (`WorkOut.standing`, `inventory/approval_standing.py`); the browser decides nothing.
 *
 * **No reason is read back.** `approval_lists` rule 4 stands: a reviewer's reason is free text that
 * may name a person, and no accepted decision says who may read it back. The card names the decision,
 * the person, the stage, the time and the entry, and nothing the person wrote.
 *
 * **Stale is a sentence and a list of differences.** The server compares the revision and figures
 * that were approved with the ones in force and sends exactly what moved. This card prints that list
 * and nothing else: the re-approval view shows what changed, not the record again. A sign-off is never
 * deleted or restyled as rejected when it goes stale: nobody's judgement was overturned, the thing
 * judged was replaced (EXP-32).
 */

import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useReader } from "../api/profile";
import type { Work } from "../api/work";
import type { GridColumn } from "../components/ui";
import { GridTable, InfoPopover, StatusChip } from "../components/ui";
import { instantStamp, quantity, readerLocale, tonnes, unitLabel } from "../lib/format";
import type { Reader } from "../lib/format";
import { keepSearch } from "./search";
import own from "./RecordSignOff.module.css";

export type Standing = NonNullable<Work["standing"]>;
type Signoff = Standing["signoffs"][number];
type Change = Standing["changes"][number];

/** The words a stale approval carries. One home, so the card, the status path and the tests agree. */
export const STALE_SENTENCE = "Stale: the figure changed after this approval";

const DECISION_WORD: Readonly<Record<string, string>> = {
  approve: "Approved",
  reject: "Rejected",
  request_adjustment: "Asked for a change",
};

const ROLE_WORD: Readonly<Record<string, string>> = {
  admin: "Admin",
  approver: "Approver",
  contributor: "Contributor",
  auditor: "Auditor",
  viewer: "Viewer",
};

function value(change: Change, side: "was" | "now", onReceipt: (calculationId: string) => void): ReactNode {
  const raw = change[side];
  if (raw === null || raw === undefined) return "—";
  if (change.kind === "figure") {
    const text = `${tonnes(raw, 2)} tCO₂e`;
    // Every figure keeps its receipt (rule 3), and the superseded one still resolves.
    const id = side === "was" ? change.was_calculation_id : change.now_calculation_id;
    return id ? (
      <button
        type="button"
        className={own.figure}
        data-testid={`change-${side}-receipt`}
        aria-label={`Receipt for the ${side === "was" ? "approved" : "current"} figure: ${tonnes(raw, 2)} tonnes CO2 equivalent`}
        onClick={() => onReceipt(id)}
      >
        {text}
      </button>
    ) : (
      text
    );
  }
  if (change.key === "quantity") return `${quantity(raw)} ${unitLabel(change.unit ?? "")}`.trim();
  return raw.replace(/_/g, " ");
}

function changeColumns(onReceipt: (calculationId: string) => void): GridColumn<Change>[] {
  return [
    { id: "what", header: "Changed", rowHeader: true, cell: (change) => change.label },
    { id: "was", header: "Approved", numeric: true, cell: (change) => value(change, "was", onReceipt) },
    { id: "now", header: "Now", numeric: true, cell: (change) => value(change, "now", onReceipt) },
  ];
}

/** Only what moved since the approval. Empty is a sentence of its own: a revision that replaced the
 * approved one with nothing different in the fields compared is still a replacement. */
export function ChangesSince({
  standing,
  onReceipt,
}: {
  standing: Standing;
  onReceipt: (calculationId: string) => void;
}) {
  if (standing.changes.length === 0) {
    return (
      <p className={own.note} data-testid="changes-none">
        Revision {standing.current_revision} replaced revision {standing.revision}. None of the
        fields compared differ.
      </p>
    );
  }
  return (
    <GridTable
      caption={`What changed since revision ${String(standing.revision)} was approved`}
      columns={changeColumns(onReceipt)}
      rows={standing.changes}
      rowKey={(change) => `${change.kind}-${change.key}`}
      rowNumbers={false}
      zebra={false}
      testId="changes-since"
    />
  );
}

function Stamp({ iso }: { iso: string }) {
  const reader = useReader();
  // The UTC time is given a reader that says UTC, not none: the formatting layer is always handed
  // one (`readerPreferences.test.tsx`), and "UTC, labelled UTC" is a reader's choice made on purpose.
  const utcReader: Reader = { locale: reader?.locale ?? readerLocale(), timeZone: "UTC" };
  const utc = instantStamp(iso, utcReader);
  const local = reader?.timeZone && reader.timeZone !== "UTC" ? instantStamp(iso, reader) : null;
  return (
    <span className={own.when}>
      <time dateTime={iso}>{utc}</time>
      {local ? <small> · {local}</small> : null}
    </span>
  );
}

function Entry({ entry }: { entry: Signoff }) {
  return (
    <li className={own.entry} data-testid={`signoff-${String(entry.position)}-${entry.decision}`}>
      <span className={own.who}>
        <b>{entry.decided_by_name ?? "A member"}</b>
        {entry.decided_by_role ? (
          <small> · {ROLE_WORD[entry.decided_by_role] ?? entry.decided_by_role}</small>
        ) : null}
      </span>
      <span>
        {DECISION_WORD[entry.decision] ?? entry.decision}
        {entry.stage_name ? ` · ${entry.stage_name}` : ""}
      </span>
      <Stamp iso={entry.decided_at} />
      {entry.ledger_seq === null ? (
        <span className={own.muted}>No ledger entry</span>
      ) : (
        <Link
          to="/ledger/$seq"
          params={{ seq: String(entry.ledger_seq) }}
          search={keepSearch}
          className={own.seq}
          data-testid={`signoff-seq-${String(entry.ledger_seq)}`}
        >
          Ledger #{entry.ledger_seq}
        </Link>
      )}
    </li>
  );
}

export function SignOffCard({
  standing,
  onReceipt,
}: {
  standing: Standing;
  onReceipt: (calculationId: string) => void;
}) {
  return (
    <section className="card" data-testid="signoff-card" data-stale={standing.stale ? "true" : "false"}>
      <h2 className={own.head}>
        Sign-off
        <small className={own.muted}>
          {standing.route_name} · revision {standing.revision}
        </small>
        <InfoPopover infoKey="record.sign-off" />
        <span className={own.sp} />
        {standing.stale ? (
          <span className={own.stale} data-testid="stale-approval">
            <StatusChip tone="warn" size="sm">
              {STALE_SENTENCE}
            </StatusChip>
            <InfoPopover infoKey="record.stale-approval" />
          </span>
        ) : (
          <StatusChip tone="ok" size="sm">
            Still holds
          </StatusChip>
        )}
      </h2>
      <ul className={own.entries} aria-label="Who decided, and when">
        {standing.signoffs.map((entry) => (
          <Entry key={`${String(entry.position)}-${entry.decision}-${entry.decided_at}`} entry={entry} />
        ))}
      </ul>
      {standing.stale ? <ChangesSince standing={standing} onReceipt={onReceipt} /> : null}
    </section>
  );
}
